import { useTranslation } from 'react-i18next'
import { gql } from '@apollo/client'
import { useState } from 'react'
import client, { deserializeErrors } from 'apollo-client'
import { LANG_LIST } from '../use-advertisements'

export const DEFAULT_ADVERTISEMENT = {
  id: null,
  privateTitle: '',
  deployDate: {
    start: null,
    end: null
  },
  contents: LANG_LIST.map(value => ({
    language: value,
    header: '',
    body: '',
    footer: ''
  })),
  image: null,
  accounts: []
}

const OPERATION_CREATE = gql`
  mutation CreateAdvertisement($advertisement: InputPortalAdvertisement!) {
    advertisement: createPortalAdvertisement(advertisement: $advertisement) {
      name
      status
      id
      starts_at
      ends_at
      fallback_language
      creator {
        first_name
        last_name
      }
      count_sites
      count_accounts
    }
  }
`

export const OPERATION_UPDATE = gql`
  mutation UpdateAdvertisement($id: ID!, $advertisement: InputPortalAdvertisement!) {
    advertisement: updatePortalAdvertisement(id: $id, advertisement: $advertisement) {
      name
      status
      id
      starts_at
      ends_at
      fallback_language
      creator {
        first_name
        last_name
      }
      count_sites
      count_accounts
      accounts_summary {
        name
        sf_id
        sites {
          sf_id
          name
        }
      }
    }
  }
`

const serializeAdvertisement = values => {
  return {
    id: values.id,
    name: values.name,
    starts_at: values.starts_at,
    ends_at: values.ends_at,
    image: values.image?.dataURL?.indexOf('http') === -1 ? values.image : null,
    fallback_language: values.fallback_language,
    accounts: values.accounts,
    contents: values.contents.map(content => ({
      language: content.language,
      header: content.header,
      body: content.body,
      footer: content.footer
    }))
  }
}

const isPerimeterError = ({ graphQLErrors }) => {
  return Boolean(graphQLErrors[0]?.extensions.response?.body?.error === 'Errors::InsufficientPerimeterVisibility')
}
const isOverlappingError = ({ graphQLErrors }) => {
  return Boolean(
    graphQLErrors[0]?.extensions.response?.body?.error === 'Errors::PortalAdvertisementDateIntervalUnavailable'
  )
}

const useSave = () => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const saveAdvertisement = async ({ id, ...advertisement }) => {
    let response
    if (id) {
      response = await client.mutate({
        mutation: OPERATION_UPDATE,
        variables: { id, advertisement },
        refetchQueries: ['Advertisements', 'Advertisement']
      })
    } else {
      response = await client.mutate({
        mutation: OPERATION_CREATE,
        variables: {
          advertisement
        },
        refetchQueries: ['Advertisements', 'Advertisement']
      })
    }
    return response.data.advertisement
  }

  const save = async values => {
    setLoading(true)
    setError(null)
    const advertisement = serializeAdvertisement(values)
    try {
      const newContact = await saveAdvertisement(advertisement)
      return newContact
    } catch (e) {
      if (isOverlappingError(e)) {
        setError(t('portal_advertisements.errors.overlapping_advertisements.label'))
      } else if (isPerimeterError(e)) {
        setError(t('portal_advertisements.errors.perimeter.label'))
      } else {
        setError(deserializeErrors(e))
      }
    } finally {
      setLoading(false)
    }
  }
  return [save, { loading, error }]
}

export default useSave
